import React from "react";
import { Card, Tree, Space, Flex, Tag, Alert, Spin } from "antd";
import { FolderOpenOutlined, FileTextOutlined, FolderOutlined } from "@ant-design/icons";
import type { DataNode } from "antd/es/tree";
import { useTranslation } from "react-i18next";
import FinanceTreeNode from "./FinanceTreeNode";

interface FinanceTreeItem {
  id: number;
  serialNumber: string;
  name: string;
  nameAr: string;
  slug: string;
  editable: boolean;
  parentId?: number | null;
  level: number;
  balance: number;
  totalCredit: number;
  totalDebit: number;
  createdAt: string;
  updatedAt: string;
  children?: FinanceTreeItem[];
}

interface FinanceTreeViewProps {
  treeData: FinanceTreeItem[];
  totalItems: number;
  loading: boolean;
  searchValue: string;
  expandedKeys: React.Key[];
  autoExpandParent: boolean;
  onExpand: (expandedKeysValue: React.Key[]) => void;
  onAdd: (item: FinanceTreeItem) => void;
  onEdit: (item: FinanceTreeItem) => void;
  onDelete: (item: FinanceTreeItem) => void;
}

// Convert FinanceTreeItem to Ant Design Tree DataNode
function convertToTreeData(
  items: FinanceTreeItem[],
  currentLang: string,
  onAdd: (item: FinanceTreeItem) => void,
  onEdit: (item: FinanceTreeItem) => void,
  onDelete: (item: FinanceTreeItem) => void,
  t: any
): DataNode[] {
  return items.map((item) => {
    const hasChildren = Boolean(item.children && item.children.length > 0);

    return {
      title: (
        <FinanceTreeNode
          item={item}
          currentLang={currentLang}
          hasChildren={hasChildren}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          t={t}
        />
      ),
      key: `${item.id}`,
      children:
        item.children && item.children.length > 0
          ? convertToTreeData(item.children, currentLang, onAdd, onEdit, onDelete, t)
          : undefined,
      icon: hasChildren ? <FolderOutlined /> : <FileTextOutlined />,
    };
  });
}

const FinanceTreeView: React.FC<FinanceTreeViewProps> = ({
  treeData,
  totalItems,
  loading,
  searchValue,
  expandedKeys,
  autoExpandParent,
  onExpand,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const antTreeData = React.useMemo(() => {
    return convertToTreeData(treeData, currentLang, onAdd, onEdit, onDelete, t);
  }, [treeData, currentLang, onAdd, onEdit, onDelete, t]);

  return (
    <Card
      variant="borderless"
      title={
        <Flex justify="space-between" align="center" wrap="wrap">
          <Space size="middle" style={{ fontSize: "18px", fontWeight: 600 }}>
            <FolderOpenOutlined style={{ fontSize: "24px", color: "#667eea" }} />
            <span className="gradient-text" style={{ fontSize: "20px" }}>
              {t("label.finance_tree_structure")}
            </span>
          </Space>
          <Space>
            <Tag
              icon={<FolderOutlined />}
              color="processing"
              style={{
                padding: "6px 14px",
                fontSize: "14px",
                borderRadius: "8px",
                fontWeight: 600,
              }}
            >
              {treeData.length} {t("label.main_categories")}
            </Tag>
            <Tag
              icon={<FileTextOutlined />}
              color="success"
              style={{
                padding: "6px 14px",
                fontSize: "14px",
                borderRadius: "8px",
                fontWeight: 600,
              }}
            >
              {totalItems} {t("label.total_items")}
            </Tag>
          </Space>
        </Flex>
      }
      style={{
        borderRadius: "16px",
      }}
      styles={{
        body: { padding: "24px" },
      }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {loading ? (
          <Spin size="large" tip={t("label.loading")}>
            <div
              style={{
                textAlign: "center",
                padding: "120px 0",
                background: "linear-gradient(135deg, #f0f2f5 0%, #fafafa 100%)",
                borderRadius: "12px",
                minHeight: "200px",
              }}
            />
          </Spin>
        ) : antTreeData.length === 0 ? (
          <Alert
            message={t("label.no_data")}
            description={
              searchValue ? t("label.no_search_results") : t("label.no_finance_tree_items")
            }
            type="info"
            showIcon
            style={{
              margin: "40px 0",
              borderRadius: "12px",
              border: "2px dashed #91d5ff",
              background: "#e6f7ff",
            }}
          />
        ) : (
          <div
            className="finance-tree-wrapper"
            style={{
              padding: "24px",
              borderRadius: "12px",
            }}
          >
            <Tree
              showLine={{
                showLeafIcon: false,
              }}
              showIcon
              defaultExpandAll={false}
              expandedKeys={expandedKeys}
              autoExpandParent={autoExpandParent}
              onExpand={onExpand}
              treeData={antTreeData}
              style={{
                background: "transparent",
                fontSize: "15px",
              }}
            />
          </div>
        )}
      </Space>
    </Card>
  );
};

export default FinanceTreeView;

